import * as React from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { handleStartSession, handleImpersonateSession } from 'src/store/authentication/authentication.actions';
import { getQueryParam } from 'src/utilities/queryParams';
import CookieLocalStorage from 'src/features/Auth/Login/CookieLocalStorage';
import { equals, pathOr } from 'ramda';
import { isNotEmpty } from 'src/utilities/hasValue';
import { COOKIE } from 'src/services/auth/auth';
import SiteSettings from 'src/siteSetting';
import { unAuthenticatedUser } from 'src/utilities/storage';
import { IN_BRANCH_SIGNUP } from 'src/features/constants';


/**
 * If this URL doesn't have a fragment, or doesn't have enough entries, we know we don't have
 * the data we need and should bounce.
 * location.hash is a string which starts with # and is followed by a basic query params stype string.
 *
 * 'location.hash = `#access_token=something&token_type=something˙&expires_in=something&scope=something&state=something&return=the-url-we-are-now-at?returnTo=where-to-redirect-when-done`
 *
 */
const authenticateAndRedirect = (
  history: any,
  location: any,
  dispatchFn: any
) => {

  const locationState = pathOr({}, ['state'], location);

  const {
    user = {},
    rememberMe,
    advisorUsername,
    productType,
    signupType
  } = locationState as any;

  const hasAuth = isNotEmpty(user.token) || (isNotEmpty(user.id) && equals(SiteSettings.getCoreAPIAuthProvider(), COOKIE))

  // Checks is lender already logged in and remembered on session
  const hasLenderAlreadyLoggedIn = !CookieLocalStorage.isUserEquals(pathOr('', ['email'], user)) &&
    CookieLocalStorage.isUserRemembered() &&
    CookieLocalStorage.isLender()

  if (hasAuth && rememberMe && !hasLenderAlreadyLoggedIn) {
    CookieLocalStorage.setUser({ user, remember: rememberMe });
  }
  /**
   * If the hash doesn't contain a string after the #, there's no point continuing as we dont have
   * the query params we need.
   */
  /** If the access token wasn't returned, something is wrong and we should bail. */

  if (!location || !user || !user.id || !hasAuth) {
    return history.push('/');
  }
  //const { id,token, email, context } = user;
  const { id, token, email, role, team_id, roles_id, signupType: loginType, productType: loginProductType } = user;



  /** Get the name fo the user  */
  const { name } = user.object_meta;

  /** Get the role registered for the user  */
  // const role = pathOr(DEFAULT_ROLE_NAME, ['role', META_DATA], user);
  /**
   * Build the path we're going to redirect to after we're done (back to where the user was when they started authentication).
   * This has to be handled specially; the hashParams object above already has a "return" property, but query parsers
   * don't handle URLs as query params very well. Any query params in the returnTo URL will be parsed as if they were separate params.
   */

  // Find the returnTo= param directly
  const returnPath = getQueryParam(location.search, 'returnTo');
  // If this worked, we have a return URL. If not, default to the root path.
  const returnTo = returnPath ?? '/';

  /**
   * We multiply the expiration time by 1000 ms because JavaSript returns time in ms, while
   * the API returns the expiry time in seconds
   * Set at 4.167 Hrs
   */
  const expireDate = new Date();
  const expiresIn = 15000;
  expireDate.setTime(expireDate.getTime() + +expiresIn * 1000);

  /**
   * We have all the information we need and can persist it to localStorage and Redux.
   */
  dispatchFn(
    handleStartSession({
      accountId: id,
      accountEmail: email,
      accountName: name,
      loggedInRole: role,
      token: token,
      teamId: team_id,
      expires: expireDate.toString(),
      advisorUsername: advisorUsername || unAuthenticatedUser.advisorUsername.get(),
      productType: productType || loginProductType,
      rolesId: roles_id,
      signupType: signupType || loginType
    })
  );

  /**
   * All done, redirect this bad-boy to the returnTo URL we generated earlier.
   */

  history.push(returnTo);
}


const impersonateAndRedirect = (
  history: any,
  location: any,
  dispatchFn: any
) => {
  const locationState = pathOr({}, ['state'], location);

  const {
    user = {},
    advisorUsername,
    productType
  } = locationState as any;
  const { user: sessionUser } = CookieLocalStorage.getUser()

  
  const expireDate = new Date();
  const expiresIn = 15000;
  expireDate.setTime(expireDate.getTime() + +expiresIn * 1000);
  const { productType: loginProductType } = user;


  dispatchFn(
    handleImpersonateSession(
      {
        impersonator: {
          accountId: pathOr('', ['id'], sessionUser),
          accountEmail: pathOr('', ['email'], sessionUser),
          accountName: pathOr('', ['first_name'], sessionUser),
          loggedInRole: pathOr('', ['role'], sessionUser),
          rolesId: pathOr([], ['roles_id'], sessionUser),
          token: pathOr('', ['token'], sessionUser),
          teamId: pathOr('', ['team_id'], sessionUser),
          expires: expireDate.toString(),
        },
        impersonatedUser: {
          applicantAccountId: pathOr('', ['id'], user),
          applicantAccountEmail: pathOr('', ['email'], user),
          applicantTeamId: pathOr('', ['team_id'], user),
          applicantRolesId: pathOr([], ['roles_id'], user),
          advisorUsername: advisorUsername || unAuthenticatedUser.advisorUsername.get(),
          productType: productType || loginProductType,
          signupType: IN_BRANCH_SIGNUP
        }
      }
    )
  )

  // Find the returnTo= param directly
  const returnPath = getQueryParam(location.search, 'returnTo');
  // If this worked, we have a return URL. If not, default to the root path.
  const returnTo = returnPath ?? '/';
  history.push(returnTo);

}


const LoggedInCallbackPage: React.FC = () => {

  const history = useHistory();
  const dispatch = useDispatch()
  const location = useLocation()

  React.useEffect(() => {
    const email = pathOr('', ['state', 'user', 'email'], location)
    if (isNotEmpty(email) && CookieLocalStorage.isUserRemembered() && !CookieLocalStorage.isUserEquals(email)) {
      impersonateAndRedirect(
        history,
        location,
        dispatch
      )
      return
    }
    authenticateAndRedirect(
      history,
      location,
      dispatch
    )
  }, [])

  return null
}



export default LoggedInCallbackPage
